import React, { useState, useEffect } from "react";
import { X, ChevronDown, ChevronRight } from "lucide-react";
import { UserIcon as User, TrainIcon, ReportIcon } from "@/components/ui/Icons";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { pageRoutes } from "@/lib/pageRoutes";
import { CommonLabel } from "./ui/common-label";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "@/contexts/AuthContext";

export function Sidebar({ isCollapsed, isMobile, setSidebarCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, userProfile } = useAuth();

  const currentPath = location.pathname;
  const isRakeActive = ['/rake-movements', '/rake-points-management', '/rake-receipts', '/missing-wagon-receipts'].includes(currentPath);
  const isReportsActive = ['/reports-analytics', '/reports-secondary-trip', '/reports-rake-movement'].includes(currentPath);

  const [isRakeOpen, setIsRakeOpen] = useState(isRakeActive);
  const [isReportsOpen, setIsReportsOpen] = useState(isReportsActive);

  useEffect(() => {
    if (isRakeActive) {
      setIsRakeOpen(true);
    }
  }, [currentPath]);

  useEffect(() => {
    if (isReportsActive) {
      setIsReportsOpen(true);
    }
  }, [currentPath]);

  const role = (userProfile?.role || userRole || '').toLowerCase();

  const canAccess = (id) => {
    if (!role) return true;
    const roleLower = role.toLowerCase();

    if (id === 'rake-movements') {
      return roleLower === 'port_admin' || roleLower === 'ipl_super_admin' || roleLower === 'ipl_admin';
    }
    if (id === 'rake-points-management') {
      return roleLower === 'ipl_super_admin' || roleLower === 'ipl_admin';
    }
    if (id === 'rake-receipts' || id === 'missing-wagon-receipts') {
      return ['region_head', 'account_head', 'field_officer'].includes(roleLower) || roleLower === 'ipl_super_admin' || roleLower === 'ipl_admin';
    }
    if (id === 'reports-analytics' || id === 'reports-secondary-trip' || id === 'reports-rake-movement') {
      return ['ipl_super_admin', 'ipl_admin', 'port_admin'].includes(roleLower);
    }

    if (roleLower === 'ipl_super_admin' || roleLower === 'ipl_admin') return true;
    if (roleLower === 'port_admin') return id !== 'rake-points-management' && id !== 'rake-receipts';
    if (roleLower === 'transporter') return ['live-tracking', 'trip-management', 'user-management', 'vehicles-management'].includes(id);
    if (roleLower === 'distributor' || roleLower === 'retailer') return id === 'trip-management';
    if (roleLower === 'region_head') return ['rake-receipts', 'missing-wagon-receipts', 'trip-management'].includes(id);
    if (roleLower === 'field_officer') return ['rake-receipts', 'missing-wagon-receipts', 'trip-management', 'user-management'].includes(id);
    if (roleLower === 'account_head') return ['rake-receipts', 'missing-wagon-receipts'].includes(id);
    if (roleLower === 'account_officer') return id === 'correction-tickets';
    return true;
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) setSidebarCollapsed(true);
  };

  const isTabActive = (path) => {
    if (path === '/') return currentPath === '/' || currentPath === '/dashboard';
    return currentPath === path;
  };

  const renderNavItem = (id, path, title, Icon) => {
    if (!canAccess(id)) return null;

    const active = isTabActive(path);
    const label = (role === 'transporter' && id === 'user-management') ? 'Driver Management' : title;

    if (isCollapsed) {
      return (
        <button
          key={id}
          onClick={() => handleNavigate(path)}
          className="group relative flex h-11 w-11 items-center justify-center transition-all active:scale-95 mx-auto text-muted-foreground/80 hover:text-foreground"
        >
          {!active && <div className="absolute h-10 w-10 rounded-[10px] bg-secondary/60 opacity-0 group-hover:opacity-100 transition-all duration-200" />}
          {active && (
            <motion.div layoutId="activeDesktopTab" className="absolute h-10 w-10 rounded-2xl bg-primary"
              initial={{ scale: 0.8 }} animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
          )}
          <div className="relative z-10 transition-transform group-hover:scale-110 duration-200 flex items-center justify-center">
            <Icon size={22} color={active ? "#FFFFFF" : "currentColor"} strokeWidth={active ? 2.5 : 2}
              className={cn("flex-shrink-0 transition-colors", !active && "text-muted-foreground")}
            />
          </div>
          <span className="absolute left-full ml-4 hidden rounded-lg border px-3 py-2 text-[12px] font-semibold text-primary shadow-xl group-hover:block z-[9999] whitespace-nowrap bg-card border-border animate-in fade-in slide-in-from-left-2 duration-200">
            {label}
          </span>
        </button>
      );
    }

    return (
      <button
        key={id}
        onClick={() => handleNavigate(path)}
        className={cn(
          "w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition-colors text-sm font-medium",
          active ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground/85 hover:text-foreground hover:bg-secondary/60"
        )}
      >
        <Icon size={20} strokeWidth={2} className="flex-shrink-0" />
        <CommonLabel size="sm" weight="medium" className="whitespace-nowrap">{label}</CommonLabel>
      </button>
    );
  };

  const renderSubItem = (id, path, title, Icon) => {
    if (!canAccess(id)) return null;
    const active = isTabActive(path);

    return (
      <button
        key={id}
        onClick={() => handleNavigate(path)}
        className={cn(
          "w-full flex items-center gap-2 pl-8 pr-2.5 py-1.5 rounded-lg transition-colors text-[13px] font-medium text-left",
          active ? "text-primary font-semibold" : "text-muted-foreground/80 hover:text-foreground"
        )}
      >
        <Icon size={16} strokeWidth={2} className={cn("flex-shrink-0", active ? "text-primary" : "text-muted-foreground")} />
        <span className="whitespace-nowrap">{title}</span>
      </button>
    );
  };

  const showRakeGroup = canAccess('rake-movements') || canAccess('rake-points-management') || canAccess('rake-receipts') || canAccess('missing-wagon-receipts');
  const showReportsGroup = canAccess('reports-analytics') || canAccess('reports-secondary-trip') || canAccess('reports-rake-movement');

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex-grow">
        <div className={cn("px-3 py-4", isCollapsed && "p-2 px-1.5")}>
          <div className={cn("flex items-center gap-3 mb-6 h-8", isCollapsed && "justify-center")}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/10 p-1">
              <img src="https://ipl-fleet-tracking.s3.ap-south-1.amazonaws.com/src/logo.png" alt="IPL VLTS Tracking Logo" className="object-contain mx-auto" />
            </div>
            {!isCollapsed && (
              <CommonLabel size="xl" weight="bold" className="text-foreground whitespace-nowrap">IPL VLTS</CommonLabel>
            )}
          </div>

          <nav className="flex flex-col gap-1">
            {renderNavItem('dashboard', '/', pageRoutes.dashboard.title, pageRoutes.dashboard.icon)}
            {renderNavItem('live-tracking', '/live-tracking', pageRoutes['live-tracking'].title, pageRoutes['live-tracking'].icon)}
            {renderNavItem('user-management', '/user-management', pageRoutes['user-management'].title, pageRoutes['user-management'].icon)}
            {renderNavItem('trip-management', '/trip-management', pageRoutes['trip-management'].title, pageRoutes['trip-management'].icon)}

            {showRakeGroup && (
              isCollapsed ? (
                <>
                  {renderNavItem('rake-movements', '/rake-movements', pageRoutes['rake-movements'].title, pageRoutes['rake-movements'].icon)}
                  {renderNavItem('rake-points-management', '/rake-points-management', pageRoutes['rake-points-management'].title, pageRoutes['rake-points-management'].icon)}
                  {renderNavItem('rake-receipts', '/rake-receipts', pageRoutes['rake-receipts'].title, pageRoutes['rake-receipts'].icon)}
                  {renderNavItem('missing-wagon-receipts', '/missing-wagon-receipts', pageRoutes['missing-wagon-receipts'].title, pageRoutes['missing-wagon-receipts'].icon)}
                </>
              ) : (
                <div className="my-0.5">
                  <button
                    type="button"
                    onClick={() => setIsRakeOpen(!isRakeOpen)}
                    className={cn(
                      "w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition-colors text-sm font-medium select-none cursor-pointer",
                      isRakeActive ? "text-primary font-semibold" : "text-muted-foreground/85 hover:text-foreground hover:bg-secondary/60"
                    )}
                  >
                    <TrainIcon size={20} strokeWidth={2} className="flex-shrink-0" />
                    <CommonLabel size="sm" weight="medium" className="whitespace-nowrap flex-1 text-left cursor-pointer">Rake Management</CommonLabel>
                    {isRakeOpen ? <ChevronDown className="w-4 h-4 text-primary" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>

                  <AnimatePresence initial={false}>
                    {isRakeOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden space-y-0.5 mt-0.5"
                      >
                        {renderSubItem('rake-movements', '/rake-movements', pageRoutes['rake-movements'].title, pageRoutes['rake-movements'].icon)}
                        {renderSubItem('rake-points-management', '/rake-points-management', pageRoutes['rake-points-management'].title, pageRoutes['rake-points-management'].icon)}
                        {renderSubItem('rake-receipts', '/rake-receipts', pageRoutes['rake-receipts'].title, pageRoutes['rake-receipts'].icon)}
                        {renderSubItem('missing-wagon-receipts', '/missing-wagon-receipts', pageRoutes['missing-wagon-receipts'].title, pageRoutes['missing-wagon-receipts'].icon)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            )}

            {renderNavItem('correction-tickets', '/correction-tickets', pageRoutes['correction-tickets'].title, pageRoutes['correction-tickets'].icon)}

            {showReportsGroup && (
              isCollapsed ? (
                <>
                  {renderNavItem('reports-analytics', '/reports-analytics', pageRoutes['reports-analytics'].title, pageRoutes['reports-analytics'].icon)}
                  {renderNavItem('reports-secondary-trip', '/reports-secondary-trip', pageRoutes['reports-secondary-trip'].title, pageRoutes['reports-secondary-trip'].icon)}
                  {renderNavItem('reports-rake-movement', '/reports-rake-movement', pageRoutes['reports-rake-movement'].title, pageRoutes['reports-rake-movement'].icon)}
                </>
              ) : (
                <div className="my-0.5">
                  <button
                    type="button"
                    onClick={() => setIsReportsOpen(!isReportsOpen)}
                    className={cn(
                      "w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition-colors text-sm font-medium select-none cursor-pointer",
                      isReportsActive ? "text-primary font-semibold" : "text-muted-foreground/85 hover:text-foreground hover:bg-secondary/60"
                    )}
                  >
                    <ReportIcon size={20} strokeWidth={2} className="flex-shrink-0" />
                    <CommonLabel size="sm" weight="medium" className="whitespace-nowrap flex-1 text-left cursor-pointer">Reports</CommonLabel>
                    {isReportsOpen ? <ChevronDown className="w-4 h-4 text-primary" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>

                  <AnimatePresence initial={false}>
                    {isReportsOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden space-y-0.5 mt-0.5"
                      >
                        {renderSubItem('reports-analytics', '/reports-analytics', pageRoutes['reports-analytics'].title, pageRoutes['reports-analytics'].icon)}
                        {renderSubItem('reports-secondary-trip', '/reports-secondary-trip', pageRoutes['reports-secondary-trip'].title, pageRoutes['reports-secondary-trip'].icon)}
                        {renderSubItem('reports-rake-movement', '/reports-rake-movement', pageRoutes['reports-rake-movement'].title, pageRoutes['reports-rake-movement'].icon)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            )}

            {renderNavItem('ports-management', '/ports-management', pageRoutes['ports-management'].title, pageRoutes['ports-management'].icon)}
            {renderNavItem('products-management', '/products-management', pageRoutes['products-management'].title, pageRoutes['products-management'].icon)}
            {renderNavItem('vehicles-management', '/vehicles-management', pageRoutes['vehicles-management'].title, pageRoutes['vehicles-management'].icon)}
          </nav>
        </div>
      </div>

      <div className={cn("px-3 py-4 mt-auto", isCollapsed && "p-2 px-1.5")}>
        <div className="space-y-1">
          {(() => {
            const active = currentPath === '/profile';
            if (isCollapsed) {
              return (
                <button
                  onClick={() => handleNavigate('/profile')}
                  className="group relative flex h-11 w-11 items-center justify-center transition-all active:scale-95 mx-auto text-muted-foreground/80 hover:text-foreground"
                >
                  {!active && <div className="absolute h-10 w-10 rounded-[10px] bg-secondary/60 opacity-0 group-hover:opacity-100 transition-all duration-200" />}
                  {active && (
                    <motion.div layoutId="activeDesktopTab" className="absolute h-10 w-10 rounded-2xl bg-primary"
                      initial={{ scale: 0.8 }} animate={{ scale: 1 }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <div className="relative z-10 transition-transform group-hover:scale-110 duration-200 flex items-center justify-center">
                    <User size={22} color={active ? "#FFFFFF" : "currentColor"} strokeWidth={active ? 2.5 : 2}
                      className={cn("flex-shrink-0 transition-colors", !active && "text-muted-foreground")}
                    />
                  </div>
                  <span className="absolute left-full ml-4 hidden rounded-lg border px-3 py-2 text-[12px] font-semibold text-primary shadow-xl group-hover:block z-[9999] whitespace-nowrap bg-card border-border animate-in fade-in slide-in-from-left-2 duration-200">
                    Profile
                  </span>
                </button>
              );
            }
            return (
              <button
                onClick={() => handleNavigate('/profile')}
                className={cn("w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition-colors text-sm font-medium",
                  active ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground/85 hover:text-foreground hover:bg-secondary/60"
                )}
                title="Profile"
              >
                <User size={20} strokeWidth={2} className="flex-shrink-0" />
                <CommonLabel size="sm" weight="medium" className="whitespace-nowrap">Profile</CommonLabel>
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <AnimatePresence>
        {!isCollapsed && (
          <>
            <motion.div className="fixed inset-0 bg-black/60 z-40"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }} onClick={() => setSidebarCollapsed(true)}
            />
            <motion.aside className="fixed top-0 left-0 h-full bg-card z-50 flex flex-col shadow-[0px_0px_16px_rgba(17,17,26,0.05)]"
              initial={{ x: "-100%", width: isCollapsed ? "4rem" : "15rem" }}
              animate={{ x: 0, width: isCollapsed ? "4rem" : "15rem" }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.2, ease: "easeInOut" }}
            >
              <div className="p-2 absolute top-2 right-2">
                <Button variant="ghost" size="icon" onClick={() => setSidebarCollapsed(true)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    <motion.aside
      className="fixed top-0 left-0 h-full bg-card z-30 flex flex-col shadow-[0px_0px_16px_rgba(17,17,26,0.05)]"
      initial={{ width: isCollapsed ? "4rem" : "15rem" }}
      animate={{ width: isCollapsed ? "4rem" : "15rem" }}
      transition={{ type: "tween", duration: 0.2, ease: "easeInOut" }}
    >
      {sidebarContent}
    </motion.aside>
  );
}